<template>
    <Teleport to="#tesco-voucher-uk-payment-method-vue-app">
        <div id="tesco-voucher-uk-payment-method">
            <CheckClubcardTokenForm ref="check-clubcard-token-form"
                                    btn-color="default"
                                    @loaded="onTokenLoaded" />

            <!-- Button to use the token, if possible. -->
            <div v-if="token"
                 class="mt-1 text-right">
                <SquareBtn :disabled="!token.redeemable || !payment"
                           size="xl"
                           color="primary"
                           :loading="applying"
                           @click="useToken">
                    {{ useVoucherButtonText }}
                </SquareBtn>
            </div>

            <ErrorMessage v-if="error"
                          :error="error"
                          class="mt-1" />
        </div>
    </Teleport>
</template>

<script>
import CheckClubcardTokenForm from '@/epos/vue/modals/Search/CheckClubcardTokenForm';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import EventNames from '@/lib/Events/EventNames';
import SquareBtn from '@/vue/components/SquareBtn';
import UserFacingError from '@/libraries/DigiTickets/Errors/UserFacingError';
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';

export default {
    name: 'TescoVoucherUkPaymentMethod',

    components: {
        CheckClubcardTokenForm,
        ErrorMessage,
        SquareBtn,
    },

    data() {
        return {
            /**
             * @type {boolean}
             */
            applying: false,

            /**
             * @type {UserFacingError}
             */
            error: null,

            /**
             * @type {?ClubcardToken}
             */
            token: null,

            /**
             * @type {?ClubcardTokenWithItems}
             */
            tokenWithItems: null,

            /**
             * @type {?Payment}
             */
            payment: null,
        };
    },

    computed: {
        useVoucherButtonText() {
            if (this.payment) {
                return container.langService.getText('PAYMENT.USE_VOUCHER_AMOUNT', { amount: currencyFilter(this.payment.tendered) });
            }

            return container.langService.getText('PAYMENT.USE_VOUCHER');
        },
    },

    created() {
        this.deregisterCartPaymentsChangedListenerFunc = container.$rootScope.$on(
            EventNames.CART_PAYMENTS_CHANGED,
            () => {
                this.payment = this.tokenWithItems ? this.generatePayment(this.tokenWithItems) : null;
            }
        );
    },

    beforeUnmount() {
        if (this.deregisterCartPaymentsChangedListenerFunc) {
            this.deregisterCartPaymentsChangedListenerFunc();
        }
    },

    methods: {
        reset() {
            // Resetting the form will trigger onTokenLoaded and clear the selected token.
            this.$refs['check-clubcard-token-form'].reset();
        },

        clearError() {
            this.error = null;
        },

        /**
         * @param {ClubcardTokenWithItems} tokenWithItems
         */
        onTokenLoaded(tokenWithItems) {
            this.clearError();
            this.tokenWithItems = tokenWithItems;
            this.token = tokenWithItems ? tokenWithItems.token : null;
            try {
                this.payment = tokenWithItems ? this.generatePayment(tokenWithItems) : null;
            } catch (e) {
                console.error(e);
                this.error = e;
            }
        },

        /**
         * Generate a Payment that can be added to the cart for this voucher.
         * This may return null if it's not possible.
         *
         * @param {ClubcardTokenWithItems} tokenWithItems
         *
         * @return {Payment}
         *
         * @throws {UserFacingError}
         */
        generatePayment(tokenWithItems) {
            return container.clubcardTokenApplicator.generatePaymentForToken(tokenWithItems);
        },

        async useToken() {
            this.clearError();

            if (!this.tokenWithItems || !this.payment) {
                return;
            }

            const cartService = container.cartService;
            if (cartService.getRemainingBalance() <= 0) {
                return;
            }

            // If partial payments aren't allowed and the voucher doesn't have enough to cover the balance, exit
            if (
                !cartService.allowPartialPayments()
                && this.token.value < cartService.getRemainingBalance()
            ) {
                this.error = new UserFacingError('', 'PAYMENT.PARTIAL_PAYMENT_VOUCHER_ERROR');
                return;
            }

            this.applying = true;

            try {
                // Regenerate the payment now in case something changed since the test payment was created.
                const payment = this.generatePayment(this.tokenWithItems);
                payment.setThirdPartyID(cartService.generatePaymentThirdPartyID());

                // Build a new list of all the existing payments plus the new voucher payment.
                // We'll send this to the API to check if everything looks good together.
                let newPaymentsSet = new Set([
                    ...cartService.currentCart.payments,
                    payment,
                ]);

                await container.externalCartCalculator.recalculate(cartService, Array.from(newPaymentsSet));

                // The recalculate call succeeded. Add the payment to the cart.
                container.eventBus.broadcast(EventNames.ADD_PAYMENT_TO_CART_REQUESTED, payment);

                this.reset();
            } catch (error) {
                console.error(error);

                // If the recalculate API call returned an error it was unhappy with the voucher.
                // So we won't proceed. Just show an error.
                this.error = error;
            } finally {
                this.applying = false;
            }
        },
    },
};
</script>
